import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import SEO from '../components/SEO';
import Navbar from '../components/shared/Navbar';
import StoryPreviewWidget from '../components/StoryPreviewWidget';
import SchemaMarkup, { buildFaqSchema, buildBreadcrumbSchema } from '../components/SchemaMarkup';
import {
  THEMES, LANGUAGES, AGE_GROUPS, OCCASIONS, POPULAR_NAMES,
  LESSONS, STORY_LENGTHS, GENDERS, RELATIONSHIPS, COUNTRIES,
  HOLIDAYS, SIBLINGS, GIFTS, COMPARISONS, BILINGUAL,
  NAME_STORY_TITLE_TEMPLATES, ANIMALS, SETTINGS, MOODS,
} from '../utils/seoData';

const SEO_PAGE_FAQS = [
  { q: 'Is Dreamy Bedtime free?', a: 'Yes — you get 5 free personalised bedtime stories every month with no credit card required.' },
  { q: 'How quickly is a story generated?', a: 'Stories are ready in about 15 seconds. Just enter your child\'s name, age, and interests.' },
  { q: 'Can I personalise the story with my child\'s name?', a: 'Absolutely. Every story is written with your child as the main character, using their name throughout.' },
  { q: 'What languages are available?', a: 'Dreamy Bedtime supports 11 languages including English, Spanish, French, Hindi, Arabic, German, Portuguese, Chinese, Japanese, Italian, and Gujarati.' },
  { q: 'Do the stories include illustrations?', a: 'Yes. Each story comes with a beautiful AI-generated illustration tailored to the story\'s theme.' },
  { q: 'Can I listen to the story instead of reading it?', a: 'Yes. Natural narration is built in so stories can be listened to, not just read.' },
];

// ── Preview-widget helpers ────────────────────────────────────────────────────
// The interactive preview only accepts these 6 interests (backend VALID_INTERESTS),
// so map a page's theme/animal/setting text onto the closest match. Returns [] when
// nothing maps — the story still stars the child by name, which is the main hook.
const previewInterestsFor = (text) => {
  const s = String(text || '').toLowerCase();
  if (/dragon/.test(s))                                        return ['dragons'];
  if (/space|astronaut|star|planet|galaxy|rocket|moon/.test(s))return ['space'];
  if (/ocean|sea|under.?water|mermaid|fish|pirate/.test(s))    return ['ocean'];
  if (/magic|wizard|fairy|witch|spell|unicorn|enchant/.test(s))return ['magic'];
  if (/animal|fox|bear|cat|dog|lion|dino|horse|bug|bird/.test(s)) return ['animals'];
  if (/adventure|explor|quest|journey|jungle|forest/.test(s))  return ['adventure'];
  return [];
};

// Pick a representative age from an age-group range string (e.g. "7-9" → 8).
const previewAgeFor = (range) => {
  const nums = String(range || '').match(/\d+/g);
  if (nums && nums.length >= 2) return String(Math.round((Number(nums[0]) + Number(nums[1])) / 2));
  if (nums && nums.length === 1) return nums[0];
  if (/toddler|baby/i.test(String(range))) return '3';
  return '5';
};

// ── Shared CTA section ────────────────────────────────────────────────────────
function CtaSection({ heading, sub }) {
  return (
    <section className="py-5" style={{ background: 'linear-gradient(135deg,#2d1b69 0%,#4a1d96 100%)' }}>
      <div className="container text-center py-2 py-md-3 px-4">
        <div style={{ fontSize: '40px', marginBottom: '12px' }}>🌙</div>
        <h2 className="fw-bold text-white mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(22px,4vw,36px)' }}>
          {heading}
        </h2>
        <p className="mb-4 mx-auto" style={{ color: '#c9a8ff', fontSize: '16px', maxWidth: '480px' }}>
          {sub}
        </p>
        <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center align-items-center">
          <Link to="/register" className="btn btn-warning btn-lg rounded-pill fw-bold w-100 w-sm-auto" style={{ fontSize: '16px', maxWidth: '280px' }}>
            Start Free — 5 Stories/Month
          </Link>
          <Link to="/pricing" className="btn btn-outline-light btn-lg rounded-pill w-100 w-sm-auto" style={{ maxWidth: '160px' }}>
            See Pricing
          </Link>
        </div>
        <div className="d-flex flex-wrap gap-2 justify-content-center mt-3">
          {['No credit card required', 'Cancel anytime', 'Stories in 15 seconds'].map(t => (
            <span key={t} style={{ color: '#c9a8ff', fontSize: '12px' }}>
              <i className="bi bi-check-circle-fill me-1" style={{ color: '#4ade80' }}></i>{t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Feature cards ─────────────────────────────────────────────────────────────
function FeatureCards() {
  const features = [
    { icon: '✍️', title: 'Fully Personalised',     body: 'Every story stars your child by name — with their favourite characters and interests woven into every chapter.' },
    { icon: '🎙️', title: 'Natural Narration',       body: 'Listen to your story read aloud in a warm, natural voice. Available in multiple languages.' },
    { icon: '🖼️', title: 'Beautiful Illustrations',  body: "Each story comes with a unique illustration created just for your child's adventure." },
    { icon: '🌍', title: 'Multiple Languages',      body: 'Generate stories in English, Spanish, French, Hindi, Arabic, and more.' },
    { icon: '🎤', title: 'Your Voice',              body: "Clone your voice once and your child hears YOU telling the story every night — even when you're away." },
    { icon: '📚', title: 'Story Series',            body: 'Build a continuing story universe night after night with characters your child already loves.' },
  ];
  return (
    <section className="py-5 bg-white">
      <div className="container py-2 py-md-3">
        <h2 className="fw-bold text-center mb-4 mb-md-5" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(22px,4vw,34px)' }}>
          Everything you need for magical bedtimes
        </h2>
        <div className="row g-3 g-md-4">
          {features.map(f => (
            <div className="col-12 col-sm-6 col-lg-4" key={f.title}>
              <div className="h-100 p-3 p-md-4 rounded-4 border" style={{ borderColor: '#e9d5ff' }}>
                <div style={{ fontSize: '28px', marginBottom: '10px' }}>{f.icon}</div>
                <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{f.title}</h5>
                <p className="text-muted mb-0" style={{ fontSize: '14px', lineHeight: 1.6 }}>{f.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── How it works ──────────────────────────────────────────────────────────────
function HowItWorks({ childLabel }) {
  const steps = [
    { icon: '👶', title: 'Tell us about your child', body: `Enter ${childLabel}'s name, age, and favourite interests.` },
    { icon: '✨', title: 'We craft the story',        body: 'A unique, personalised story is generated in seconds — ready to read or listen to.' },
    { icon: '🌙', title: 'Enjoy magical bedtime',     body: 'Read together, listen to narration, or let them hear it in your own voice.' },
  ];
  return (
    <section className="py-5" style={{ background: 'var(--page-bg)' }}>
      <div className="container py-2 py-md-3">
        <h2 className="fw-bold text-center mb-4 mb-md-5" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(22px,4vw,34px)' }}>
          Magical stories in 3 simple steps
        </h2>
        <div className="row g-4 justify-content-center">
          {steps.map((s, i) => (
            <div className="col-12 col-md-4 text-center" key={s.title}>
              <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{ width: '64px', height: '64px', background: '#ede9fe', fontSize: '28px' }}>
                {s.icon}
              </div>
              <div className="badge rounded-pill mb-2 d-block mx-auto" style={{ background: '#7c3aed', color: '#fff', fontSize: '11px', width: 'fit-content' }}>Step {i + 1}</div>
              <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{s.title}</h5>
              <p className="text-muted mb-0" style={{ fontSize: '14px' }}>{s.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Hero section (shared structure) ──────────────────────────────────────────
// Pass `preview={{...}}` to render the interactive story preview right below the
// hero — so a searcher experiences a real personalised story before any signup.
function Hero({ badge, title, highlight, description, ctaLabel, ctaTo, visual, preview }) {
  return (
    <>
    <section className="bg-dream text-white" style={{ padding: '60px 0 50px' }}>
      <div className="container px-4">
        <div className="row align-items-center g-4">
          <div className="col-12 col-lg-7 text-center text-lg-start">
            <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill" style={{ fontSize: '12px' }}>
              {badge}
            </span>
            <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", lineHeight: 1.2, fontSize: 'clamp(28px,5vw,52px)' }}>
              {title} <span style={{ color: '#f9c74f' }}>{highlight}</span>
            </h1>
            <p className="mb-4" style={{ fontSize: 'clamp(15px,2vw,18px)', color: '#c9a8ff', lineHeight: 1.7 }}>
              {description}
            </p>
            <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start">
              <Link to={ctaTo || '/register'} className="btn btn-warning btn-lg rounded-pill fw-bold" style={{ fontSize: '16px' }}>
                {ctaLabel}
              </Link>
              <Link to="/pricing" className="btn btn-outline-light btn-lg rounded-pill">
                See Pricing
              </Link>
            </div>
          </div>
          {visual && (
            <div className="col-12 col-lg-5 text-center">
              {visual}
            </div>
          )}
        </div>
      </div>
    </section>
    {/* Value-first: every Hero-based SEO page shows the interactive preview by
        default (generic if the page doesn't customise it). Opt out with preview={false}. */}
    {preview !== false && <StoryPreviewWidget {...(preview || {})} />}
    </>
  );
}

// ── THEME PAGE ────────────────────────────────────────────────────────────────
function ThemePage({ theme }) {
  const otherThemes = THEMES.filter(t => t.slug !== theme.slug).slice(0, 8);
  return (
    <>
      <SEO
        title={`${theme.label} Bedtime Stories for Kids — Free & Personalised`}
        description={`Free personalised ${theme.label.toLowerCase()} bedtime stories starring your child as the hero. A brand-new adventure every night — read it or listen, ready in seconds.`}
        canonical={`/bedtime-stories/themes/${theme.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Story Themes', path: '/bedtime-stories/themes' },
          { name: `${theme.label} Stories`, path: `/bedtime-stories/themes/${theme.slug}` },
        ]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${theme.emoji} Personalised ${theme.label} Stories`}
        title="Personalised"
        highlight={`${theme.label} Bedtime Stories`}
        description={`${theme.description} Every story stars your child by name — created in seconds, loved forever.`}
        ctaLabel={`Create a ${theme.label} Story Free`}
        visual={<div style={{ fontSize: 'clamp(80px,15vw,120px)', lineHeight: 1 }}>{theme.emoji}</div>}
        preview={{ initialInterests: previewInterestsFor(theme.label), heading: `See a ${theme.label.toLowerCase()} story come to life`, subheading: `Type your child's name and watch a ${theme.label.toLowerCase()} bedtime story appear — free, no signup needed.` }}
      />
      <HowItWorks childLabel="your child" />

      {/* Why kids love this theme — unique per theme */}
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            Why children love {theme.label.toLowerCase()} stories
          </h2>
          <p className="text-muted mb-4" style={{ fontSize: '15px', lineHeight: 1.8, maxWidth: '720px' }}>
            {theme.whyKidsLove}
          </p>
          <h3 className="fw-bold mb-3" style={{ color: 'var(--text-main)', fontSize: 'clamp(16px,3vw,22px)' }}>
            Popular {theme.label.toLowerCase()} story ideas
          </h3>
          <div className="row g-3">
            {theme.storyTitles.map((title, i) => (
              <div className="col-12 col-md-6" key={i}>
                <div className="d-flex align-items-center gap-3 p-3 rounded-4" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                  <span style={{ fontSize: '22px', flexShrink: 0 }}>{theme.emoji}</span>
                  <span style={{ color: 'var(--text-main)', fontWeight: 600, fontSize: '14px', lineHeight: 1.4 }}>{title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Did you know — unique facts per theme */}
      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold mb-4 text-center" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(18px,3vw,26px)' }}>
            Did you know? {theme.label} facts for curious kids
          </h2>
          <div className="row g-3 justify-content-center">
            {theme.facts.map((fact, i) => (
              <div className="col-12 col-md-4" key={i}>
                <div className="p-3 p-md-4 rounded-4 h-100 bg-white" style={{ border: '1px solid #e9d5ff' }}>
                  <div className="mb-2" style={{ fontSize: '20px' }}>💡</div>
                  <p className="mb-0" style={{ color: 'var(--text-body)', fontSize: '14px', lineHeight: 1.7 }}>{fact}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FeatureCards />

      {/* Other themes */}
      <section className="py-5 bg-white">
        <div className="container py-2">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            Explore more story themes
          </h2>
          <div className="row g-2 g-md-3 justify-content-center">
            {otherThemes.map(t => (
              <div className="col-4 col-sm-3 col-md-2" key={t.slug}>
                <Link to={`/bedtime-stories/themes/${t.slug}`}
                  className="d-block text-center p-2 p-md-3 rounded-4 text-decoration-none h-100"
                  style={{ border: '2px solid #ede9fe' }}>
                  <div style={{ fontSize: '28px', marginBottom: '6px' }}>{t.emoji}</div>
                  <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '12px' }}>{t.label}</div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        heading={`Ready to create your child's ${theme.label} adventure?`}
        sub="Join thousands of families making bedtime magical every night."
      />
    </>
  );
}

// ── LANGUAGE PAGE ─────────────────────────────────────────────────────────────
function LanguagePage({ lang }) {
  const otherLangs = LANGUAGES.filter(l => l.slug !== lang.slug);
  return (
    <>
      <SEO
        title={`${lang.label} Bedtime Stories — Free & Personalised for Your Child`}
        description={`Free personalised bedtime stories in ${lang.label} (${lang.native}), starring your child by name. Perfect for bilingual families — read or listen, ready in seconds.`}
        canonical={`/bedtime-stories/languages/${lang.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Story Languages', path: '/bedtime-stories/languages' },
          { name: `${lang.label} Stories`, path: `/bedtime-stories/languages/${lang.slug}` },
        ]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${lang.emoji} Stories in ${lang.label}`}
        title="Personalised Bedtime Stories in"
        highlight={lang.label}
        description={`Create beautiful bedtime stories in ${lang.label} (${lang.native}) starring your child by name. Perfect for bilingual families and children learning ${lang.label}.`}
        ctaLabel={`Create a ${lang.label} Story Free`}
        visual={
          <div>
            <div style={{ fontSize: 'clamp(70px,12vw,100px)', lineHeight: 1 }}>{lang.emoji}</div>
            <div className="mt-2" style={{ color: '#c9a8ff', fontSize: 'clamp(20px,4vw,28px)', fontFamily: "'Fredoka One',cursive" }}>{lang.native}</div>
          </div>
        }
        preview={{ heading: 'See a personalised story come to life', subheading: "Type your child's name and watch a bedtime story appear — free, no signup needed." }}
      />

      {/* Why language matters */}
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <div className="row g-4 align-items-center">
            <div className="col-12 col-lg-6">
              <h2 className="fw-bold mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(22px,4vw,34px)' }}>
                Why bedtime stories in {lang.label} matter
              </h2>
              {[
                `Children who hear stories in their native language develop stronger literacy skills and cultural identity.`,
                `Bilingual children exposed to ${lang.label} stories at bedtime retain language better than classroom learning alone.`,
                `Hearing their own name in a ${lang.label} story makes children feel seen, loved, and proud of their heritage.`,
              ].map((point, i) => (
                <div key={i} className="d-flex gap-3 mb-3">
                  <div className="flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: '30px', height: '30px', background: '#ede9fe', color: '#7c3aed', fontWeight: 700, fontSize: '13px' }}>
                    {i + 1}
                  </div>
                  <p className="text-muted mb-0" style={{ fontSize: '14px', lineHeight: 1.7 }}>{point}</p>
                </div>
              ))}
            </div>
            <div className="col-12 col-lg-6">
              <div className="p-3 p-md-4 rounded-4" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div style={{ fontSize: '22px' }}>{lang.emoji}</div>
                  <div>
                    <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '14px' }}>Story in {lang.label}</div>
                    <div style={{ color: '#7c3aed', fontSize: '12px' }}>Personalised just for your child</div>
                  </div>
                  <span className="ms-auto badge rounded-pill" style={{ background: '#4ade80', color: '#1a4a2a', fontSize: '11px' }}>✓ Ready</span>
                </div>
                <p style={{ color: '#4a5568', fontSize: '14px', lineHeight: 1.7, margin: 0 }}>
                  Every story is written entirely in {lang.label} — the title, the plot, the moral lesson. Your child's name stays as-is. Just choose a theme and we handle the rest.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <HowItWorks childLabel="your child" />

      {/* Cultural note & story titles — unique per language */}
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <div className="row g-4 align-items-start">
            <div className="col-12 col-lg-7">
              <h2 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
                {lang.label} bedtime stories — a beautiful tradition
              </h2>
              <p className="text-muted mb-3" style={{ fontSize: '15px', lineHeight: 1.8 }}>{lang.culturalNote}</p>
              <p className="text-muted mb-0" style={{ fontSize: '15px', lineHeight: 1.8 }}>{lang.bilingualBenefit}</p>
            </div>
            <div className="col-12 col-lg-5">
              <div className="p-4 rounded-4" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                <h5 className="fw-bold mb-3" style={{ color: 'var(--text-main)', fontSize: '15px' }}>
                  {lang.emoji} Story ideas in {lang.label}
                </h5>
                {lang.storyTitles.map((title, i) => (
                  <div key={i} className="d-flex align-items-center gap-2 py-2"
                    style={{ borderBottom: i < lang.storyTitles.length - 1 ? '1px solid #e9d5ff' : 'none' }}>
                    <i className="bi bi-book flex-shrink-0" style={{ color: '#6f42c1', fontSize: '13px' }}></i>
                    <span style={{ color: 'var(--text-body)', fontSize: '14px', fontWeight: 500 }}>{title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Language facts — unique per language */}
      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold mb-4 text-center" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(18px,3vw,26px)' }}>
            Fascinating {lang.label} facts for curious minds
          </h2>
          <div className="row g-3 justify-content-center">
            {lang.facts.map((fact, i) => (
              <div className="col-12 col-md-4" key={i}>
                <div className="p-3 p-md-4 rounded-4 h-100 bg-white" style={{ border: '1px solid #e9d5ff' }}>
                  <div className="mb-2" style={{ fontSize: '20px' }}>💡</div>
                  <p className="mb-0" style={{ color: 'var(--text-body)', fontSize: '14px', lineHeight: 1.7 }}>{fact}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FeatureCards />

      {/* Other languages */}
      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            Stories available in {LANGUAGES.length} languages
          </h2>
          <div className="row g-2 g-md-3 justify-content-center">
            {otherLangs.map(l => (
              <div className="col-4 col-sm-3 col-md-2" key={l.slug}>
                <Link to={`/bedtime-stories/languages/${l.slug}`}
                  className="d-block text-center p-2 p-md-3 rounded-4 text-decoration-none"
                  style={{ border: '2px solid #e9d5ff', background: 'var(--card-bg)' }}>
                  <div style={{ fontSize: '26px', marginBottom: '4px' }}>{l.emoji}</div>
                  <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '11px' }}>{l.label}</div>
                  <div style={{ color: 'var(--text-soft)', fontSize: '10px' }}>{l.native}</div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        heading={`Start your child's ${lang.label} bedtime story tonight`}
        sub="Join thousands of families making bedtime magical in their own language."
      />
    </>
  );
}

// ── AGE GROUP PAGE ────────────────────────────────────────────────────────────
function AgeGroupPage({ ageGroup }) {
  const otherGroups = AGE_GROUPS.filter(a => a.slug !== ageGroup.slug);
  return (
    <>
      <SEO
        title={`Bedtime Stories for ${ageGroup.label} — Free, Personalised & Age-Appropriate`}
        description={`Free personalised bedtime stories for ${ageGroup.label} (ages ${ageGroup.range}), starring your child by name. Age-appropriate and magical — read or listen in seconds.`}
        canonical={`/bedtime-stories/age/${ageGroup.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: `Stories for ${ageGroup.label}`, path: `/bedtime-stories/age/${ageGroup.slug}` },
        ]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${ageGroup.emoji} Ages ${ageGroup.range}`}
        title="Bedtime Stories for"
        highlight={ageGroup.label}
        description={`${ageGroup.description} Every story is personalised with your child's name and interests.`}
        ctaLabel="Create a Story Free"
        visual={
          <div>
            <div style={{ fontSize: 'clamp(70px,12vw,100px)', lineHeight: 1 }}>{ageGroup.emoji}</div>
            <div className="mt-2 badge rounded-pill px-4 py-2" style={{ background: 'rgba(255,255,255,0.2)', color: '#f9c74f', fontSize: 'clamp(14px,3vw,18px)', fontFamily: "'Fredoka One',cursive" }}>
              Ages {ageGroup.range}
            </div>
          </div>
        }
      />

      {/* Age appropriate */}
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(22px,4vw,34px)' }}>
            Stories matched to your child's development
          </h2>
          <div className="row g-3 justify-content-center">
            {[
              { icon: '📖', title: 'Right length',     body: `Stories sized perfectly for ages ${ageGroup.range} attention spans — not too long, not too short.` },
              { icon: '💬', title: 'Right vocabulary', body: `Words and sentences calibrated for ${ageGroup.label} — building language skills naturally.` },
              { icon: '💡', title: 'Right lessons',    body: `Age-appropriate moral lessons that resonate with where your child is in life right now.` },
            ].map(f => (
              <div className="col-12 col-md-4" key={f.title}>
                <div className="text-center p-3 p-md-4 rounded-4 h-100" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>{f.icon}</div>
                  <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{f.title}</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HowItWorks childLabel="your child" />
      <FeatureCards />

      {/* Other age groups */}
      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            Stories for every age
          </h2>
          <div className="row g-2 g-md-3 justify-content-center">
            {otherGroups.map(a => (
              <div className="col-6 col-md-3" key={a.slug}>
                <Link to={`/bedtime-stories/age/${a.slug}`}
                  className="d-block text-center p-3 rounded-4 text-decoration-none"
                  style={{ border: '2px solid #e9d5ff', background: 'var(--card-bg)' }}>
                  <div style={{ fontSize: '26px', marginBottom: '6px' }}>{a.emoji}</div>
                  <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '13px' }}>{a.label}</div>
                  <div style={{ color: 'var(--text-soft)', fontSize: '11px' }}>Ages {a.range}</div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        heading={`Create your ${ageGroup.label}'s story tonight`}
        sub="Perfectly crafted for their age — personalised just for them."
      />
    </>
  );
}

// ── OCCASION PAGE ─────────────────────────────────────────────────────────────
function OccasionPage({ occasion }) {
  const otherOccasions = OCCASIONS.filter(o => o.slug !== occasion.slug).slice(0, 6);
  return (
    <>
      <SEO
        title={`${occasion.label} Bedtime Story — Free & Personalised for Your Child`}
        description={`A free, personalised bedtime story for ${occasion.label.toLowerCase()}, starring your child by name. Make the moment unforgettable — read or listen, ready in seconds.`}
        canonical={`/bedtime-stories/occasions/${occasion.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: occasion.label, path: `/bedtime-stories/occasions/${occasion.slug}` },
        ]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${occasion.emoji} ${occasion.label}`}
        title="Personalised"
        highlight={`${occasion.label} Bedtime Story`}
        description={`${occasion.description} Make this moment unforgettable with a story starring your child.`}
        ctaLabel="Create the Story Free"
        visual={<div style={{ fontSize: 'clamp(80px,14vw,110px)', lineHeight: 1 }}>{occasion.emoji}</div>}
      />

      {/* Why this matters */}
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <div className="row g-4 align-items-center">
            <div className="col-12 col-lg-6">
              <h2 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(22px,4vw,32px)' }}>
                Why a personalised story helps
              </h2>
              <p className="text-muted mb-3" style={{ fontSize: '15px', lineHeight: 1.8 }}>
                Children process big life moments through stories. A personalised bedtime story about exactly what they're going through — with them as the hero — helps them feel understood, brave, and ready for whatever comes next.
              </p>
              <p className="text-muted mb-0" style={{ fontSize: '15px', lineHeight: 1.8 }}>
                {occasion.description}
              </p>
            </div>
            <div className="col-12 col-lg-6">
              <div className="p-3 p-md-4 rounded-4" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                <div style={{ fontSize: '36px', marginBottom: '10px' }}>{occasion.emoji}</div>
                <h5 className="fw-bold mb-3" style={{ color: 'var(--text-main)', fontSize: '15px' }}>What makes our stories special</h5>
                {[
                  'Your child is the main character — by name',
                  'The story mirrors exactly what they are going through',
                  'Ends with a gentle, reassuring message',
                  'Ready in seconds — when you need it most',
                ].map((point, i) => (
                  <div key={i} className="d-flex align-items-start gap-2 mb-2">
                    <i className="bi bi-check-circle-fill mt-1 flex-shrink-0" style={{ color: '#7c3aed' }}></i>
                    <span style={{ color: '#4a5568', fontSize: '14px' }}>{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <HowItWorks childLabel="your child" />
      <FeatureCards />

      {/* Other occasions */}
      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            Stories for every special moment
          </h2>
          <div className="row g-2 g-md-3 justify-content-center">
            {otherOccasions.map(o => (
              <div className="col-6 col-md-4" key={o.slug}>
                <Link to={`/bedtime-stories/occasions/${o.slug}`}
                  className="d-block p-3 rounded-4 text-decoration-none"
                  style={{ border: '2px solid #e9d5ff', background: 'var(--card-bg)' }}>
                  <div style={{ fontSize: '22px', marginBottom: '4px' }}>{o.emoji}</div>
                  <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '13px' }}>{o.label}</div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        heading={`Create your child's ${occasion.label} story tonight`}
        sub="Make this special moment something they'll remember forever."
      />
    </>
  );
}

// ── NAME PAGE ─────────────────────────────────────────────────────────────────
function NamePage({ name }) {
  const otherNames = POPULAR_NAMES.filter(n => n.toLowerCase() !== name.toLowerCase()).slice(0, 24);
  // Pick 4 different story title templates per name based on first character code
  const titleOffset = name.charCodeAt(0) % NAME_STORY_TITLE_TEMPLATES.length;
  const storyTitles = [0, 1, 2, 3].map(i =>
    NAME_STORY_TITLE_TEMPLATES[(titleOffset + i) % NAME_STORY_TITLE_TEMPLATES.length]
      .replace(/\{name\}/g, name)
  );
  return (
    <>
      <SEO
        title={`${name}'s Bedtime Story — Free & Personalised, Ready in 15 Seconds`}
        description={`A free, personalised bedtime story starring ${name} — your child becomes the hero of their very own magical adventure. Read it or listen, ready in about 15 seconds.`}
        canonical={`/bedtime-stories/for/${name.toLowerCase()}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Names', path: '/bedtime-stories/for' },
          { name: `Stories for ${name}`, path: `/bedtime-stories/for/${name.toLowerCase()}` },
        ]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />

      <section className="bg-dream text-white" style={{ padding: '60px 0 50px' }}>
        <div className="container px-4">
          <div className="row align-items-center g-4">
            <div className="col-12 col-lg-7 text-center text-lg-start">
              <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill" style={{ fontSize: '12px' }}>
                🌙 Personalised for {name}
              </span>
              <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", lineHeight: 1.2, fontSize: 'clamp(28px,5vw,52px)' }}>
                A Bedtime Story Starring <span style={{ color: '#f9c74f' }}>{name}</span>
              </h1>
              <p className="mb-4" style={{ fontSize: 'clamp(15px,2vw,18px)', color: '#c9a8ff', lineHeight: 1.7 }}>
                Imagine <strong style={{ color: '#fff' }}>{name}'s</strong> face when they hear a bedtime story where THEY are the hero. Their name. Their favourite things. Their magical adventure — created in seconds.
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start mb-4">
                <Link to="/register" className="btn btn-warning btn-lg rounded-pill fw-bold" style={{ fontSize: '16px' }}>
                  Create {name}'s Story Free
                </Link>
              </div>
              <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-lg-start">
                {['No credit card required', 'Ready in 15 seconds', '5 free stories/month'].map(t => (
                  <span key={t} style={{ color: '#c9a8ff', fontSize: '12px' }}>
                    <i className="bi bi-check-circle-fill me-1" style={{ color: '#4ade80' }}></i>{t}
                  </span>
                ))}
              </div>
            </div>
            <div className="col-12 col-lg-5">
              <div className="p-3 p-md-4 rounded-4 mx-auto" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.2)', maxWidth: '400px' }}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: '34px', height: '34px', background: '#f9c74f', fontSize: '16px' }}>🧒</div>
                  <div className="min-w-0">
                    <div style={{ color: '#fff', fontWeight: 700, fontSize: '13px' }}>{name}'s Story</div>
                    <div style={{ color: '#c9a8ff', fontSize: '11px' }}>Personalised just for {name}</div>
                  </div>
                  <span className="ms-auto badge rounded-pill flex-shrink-0" style={{ background: '#4ade80', color: '#1a4a2a', fontSize: '10px' }}>✓ Ready</span>
                </div>
                {/* --card-bg: this preview card's title/body use --text-main and
                    --text-muted2, which invert in dark mode and vanished on white. */}
                <div className="p-3 rounded-3" style={{ background: 'var(--card-bg)' }}>
                  <div className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontFamily: "'Fredoka One',cursive", fontSize: '14px' }}>
                    {name} and the Brave Little Dragon
                  </div>
                  <p style={{ color: 'var(--text-muted2)', fontSize: '13px', lineHeight: 1.6, margin: 0 }}>
                    Once upon a time, in a land where stars could talk, a curious child named {name} discovered a tiny dragon afraid of the dark. {name} held out a hand and whispered, "I'll show you that the night is full of wonders..."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value-first: let visitors watch a real story for this name before any signup */}
      <StoryPreviewWidget
        initialName={name}
        heading={`See ${name}'s bedtime story come to life`}
        subheading={`Watch a magical story starring ${name} appear in seconds — free, no signup needed.`}
      />

      {/* Why their name matters */}
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(22px,4vw,34px)' }}>
            Why hearing their own name changes everything
          </h2>
          <div className="row g-3 justify-content-center">
            {[
              { icon: '🧠', title: 'Better engagement',   body: `Children pay significantly more attention to stories when they hear their own name — ${name} will be completely absorbed.` },
              { icon: '❤️', title: 'Feels seen and loved', body: `When ${name} is the hero of the story, they feel special, valued, and understood at the end of a long day.` },
              { icon: '😴', title: 'Falls asleep faster',  body: `Personalised stories calm children faster. ${name} will drift off to sleep feeling happy and secure.` },
            ].map(f => (
              <div className="col-12 col-md-4" key={f.title}>
                <div className="text-center p-3 p-md-4 rounded-4 h-100" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>{f.icon}</div>
                  <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{f.title}</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story title ideas — unique per name */}
      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold mb-2" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            Adventures waiting for {name} tonight
          </h2>
          <p className="text-muted mb-4" style={{ fontSize: '15px' }}>
            Every story is generated fresh — here are just a few of the magical adventures {name} could go on this evening.
          </p>
          <div className="row g-3">
            {storyTitles.map((title, i) => (
              <div className="col-12 col-md-6" key={i}>
                <div className="d-flex align-items-center gap-3 p-3 rounded-4 bg-white" style={{ border: '1px solid #e9d5ff' }}>
                  <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: '38px', height: '38px', background: 'linear-gradient(135deg,#6f42c1,#9b6fe8)', color: '#fff', fontWeight: 700, fontSize: '15px' }}>
                    {name[0]}
                  </div>
                  <span style={{ color: 'var(--text-main)', fontWeight: 600, fontSize: '14px', lineHeight: 1.4 }}>{title}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/register" className="btn btn-dream rounded-pill px-4 fw-bold" style={{ fontSize: '14px' }}>
              Generate {name}'s Story Free →
            </Link>
          </div>
        </div>
      </section>

      <HowItWorks childLabel={name} />
      <FeatureCards />

      {/* Other names */}
      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,28px)' }}>
            Stories for every child
          </h2>
          <div className="d-flex flex-wrap gap-2 justify-content-center">
            {otherNames.map(n => (
              <Link key={n} to={`/bedtime-stories/for/${n.toLowerCase()}`}
                className="btn btn-sm rounded-pill text-decoration-none"
                style={{ border: '1px solid #e9d5ff', color: '#7c3aed', background: 'var(--card-bg)', fontSize: '13px' }}>
                {n}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        heading={`Create ${name}'s magical bedtime story tonight`}
        sub={`Make ${name} the hero of their own adventure — in seconds, for free.`}
      />
    </>
  );
}

// ── INDEX PAGES ───────────────────────────────────────────────────────────────
function ThemesIndexPage() {
  return (
    <>
      <SEO
        title="Personalised Bedtime Stories by Theme — Dinosaurs, Space, Unicorns & More"
        description="Choose from 20+ magical story themes — dinosaurs, superheroes, space, unicorns, dragons and more. Every story personalised with your child's name. Try free."
        canonical="/bedtime-stories/themes"
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Story Themes', path: '/bedtime-stories/themes' }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <section className="bg-dream text-white" style={{ padding: '60px 0 50px' }}>
        <div className="container text-center px-4 py-2">
          <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(28px,5vw,50px)' }}>
            Personalised Bedtime Stories by <span style={{ color: '#f9c74f' }}>Theme</span>
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: 'clamp(15px,2vw,18px)', maxWidth: '520px', margin: '0 auto' }}>
            Pick your child's favourite theme and we'll create a unique personalised story starring them.
          </p>
        </div>
      </section>

      <StoryPreviewWidget />
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <div className="row g-3">
            {THEMES.map(t => (
              <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={t.slug}>
                <Link to={`/bedtime-stories/themes/${t.slug}`}
                  className="d-block p-3 rounded-4 text-decoration-none h-100"
                  style={{ border: '2px solid #ede9fe' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>{t.emoji}</div>
                  <h5 className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontSize: '13px' }}>{t.label}</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '11px', lineHeight: 1.4 }}>{t.description}</p>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CtaSection heading="Ready to create your child's story?" sub="Choose any theme and we'll personalise it just for them." />
    </>
  );
}

function LanguagesIndexPage() {
  return (
    <>
      <SEO
        title="Personalised Bedtime Stories in Any Language — Hindi, Spanish, French & More"
        description="Create personalised bedtime stories in 11 languages including Hindi, Spanish, French, Arabic, Chinese and more. Perfect for bilingual families."
        canonical="/bedtime-stories/languages"
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Story Languages', path: '/bedtime-stories/languages' }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <section className="bg-dream text-white" style={{ padding: '60px 0 50px' }}>
        <div className="container text-center px-4 py-2">
          <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(28px,5vw,50px)' }}>
            Bedtime Stories in <span style={{ color: '#f9c74f' }}>Any Language</span>
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: 'clamp(15px,2vw,18px)', maxWidth: '520px', margin: '0 auto' }}>
            Stories available in {LANGUAGES.length} languages — personalised with your child's name.
          </p>
        </div>
      </section>

      <StoryPreviewWidget />
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <div className="row g-3 justify-content-center">
            {LANGUAGES.map(l => (
              <div className="col-6 col-sm-4 col-md-3" key={l.slug}>
                <Link to={`/bedtime-stories/languages/${l.slug}`}
                  className="d-block p-3 rounded-4 text-decoration-none h-100"
                  style={{ border: '2px solid #ede9fe' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>{l.emoji}</div>
                  <h5 className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontSize: '14px' }}>{l.label}</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '12px' }}>{l.native}</p>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CtaSection heading="Create a story in your language tonight" sub="Perfect for bilingual families and children learning a second language." />
    </>
  );
}

function NamesIndexPage() {
  return (
    <>
      <SEO
        title="Personalised Bedtime Stories by Name — Stories Starring Your Child"
        description="Create a personalised bedtime story starring your child by name. Choose from hundreds of names or enter any name. Try free today."
        canonical="/bedtime-stories/for"
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Stories by Name', path: '/bedtime-stories/for' }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <section className="bg-dream text-white" style={{ padding: '60px 0 50px' }}>
        <div className="container text-center px-4 py-2">
          <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(28px,5vw,50px)' }}>
            A Bedtime Story Starring <span style={{ color: '#f9c74f' }}>Your Child</span>
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: 'clamp(15px,2vw,18px)', maxWidth: '520px', margin: '0 auto' }}>
            Pick your child's name and we'll create a magical story where they are the hero.
          </p>
        </div>
      </section>

      <StoryPreviewWidget />
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <div className="d-flex flex-wrap gap-2 justify-content-center">
            {POPULAR_NAMES.map(n => (
              <Link key={n} to={`/bedtime-stories/for/${n.toLowerCase()}`}
                className="btn rounded-pill text-decoration-none"
                style={{ border: '2px solid #ede9fe', color: 'var(--text-main)', background: 'var(--card-bg)', fontWeight: 600, fontSize: '14px' }}>
                {n}
              </Link>
            ))}
          </div>
          <div className="text-center mt-5">
            <p className="text-muted mb-3" style={{ fontSize: '15px' }}>Don't see your child's name? Any name works!</p>
            <Link to="/register" className="btn btn-warning btn-lg px-5 rounded-pill fw-bold">
              Create a Story with Any Name — Free
            </Link>
          </div>
        </div>
      </section>
      <CtaSection heading="Every child deserves their own story" sub="Enter any name — we'll make them the hero of a magical bedtime adventure." />
    </>
  );
}

// ── LESSON PAGE ───────────────────────────────────────────────────────────────
function LessonPage({ lesson }) {
  const others = LESSONS.filter(l => l.slug !== lesson.slug);
  return (
    <>
      <SEO
        title={`Bedtime Stories About ${lesson.label} for Kids — Free & Personalised`}
        description={`Free personalised bedtime stories about ${lesson.label.toLowerCase()}, starring your child as the hero. A gentle life lesson woven in — read it or listen, ready in seconds.`}
        canonical={`/bedtime-stories/lessons/${lesson.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: `${lesson.label} Stories`, path: `/bedtime-stories/lessons/${lesson.slug}` },
        ]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${lesson.emoji} ${lesson.label} Stories`}
        title="Bedtime Stories That Teach"
        highlight={lesson.label}
        description={`${lesson.description} Every story stars your child by name — making the lesson feel real and personal.`}
        ctaLabel={`Create a ${lesson.label} Story Free`}
        visual={<div style={{ fontSize: 'clamp(80px,15vw,120px)', lineHeight: 1 }}>{lesson.emoji}</div>}
      />
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(22px,4vw,34px)' }}>
            Why stories teach {lesson.label.toLowerCase()} better than lectures
          </h2>
          <div className="row g-3 justify-content-center">
            {[
              { icon: '🧠', title: 'Sticks in memory',    body: `Children remember stories 22x better than facts. A story about ${lesson.label.toLowerCase()} becomes part of who they are.` },
              { icon: '❤️', title: 'Feels natural',        body: `When your child is the hero learning ${lesson.label.toLowerCase()}, the lesson feels earned — not taught.` },
              { icon: '🌙', title: 'Perfect timing',       body: 'Bedtime is when children are most open to emotional learning. The right story at the right moment shapes character.' },
            ].map(f => (
              <div className="col-12 col-md-4" key={f.title}>
                <div className="text-center p-3 p-md-4 rounded-4 h-100" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>{f.icon}</div>
                  <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{f.title}</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <HowItWorks childLabel="your child" />
      <FeatureCards />
      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            More values to explore
          </h2>
          <div className="row g-2 g-md-3 justify-content-center">
            {others.map(l => (
              <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={l.slug}>
                <Link to={`/bedtime-stories/lessons/${l.slug}`}
                  className="d-block text-center p-2 p-md-3 rounded-4 text-decoration-none h-100"
                  style={{ border: '2px solid #e9d5ff', background: 'var(--card-bg)' }}>
                  <div style={{ fontSize: '26px', marginBottom: '6px' }}>{l.emoji}</div>
                  <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '12px' }}>{l.label}</div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CtaSection
        heading={`Create your child's ${lesson.label.toLowerCase()} story tonight`}
        sub="Because the best way to teach a value is to make your child the hero of the story."
      />
    </>
  );
}

// ── STORY LENGTH PAGE ─────────────────────────────────────────────────────────
function StoryLengthPage({ length }) {
  return (
    <>
      <SEO
        title={`${length.label} Bedtime Stories for Kids — ${length.minutes}-Minute Reads, Free`}
        description={`Free personalised ${length.minutes}-minute bedtime stories starring your child by name. Just the right length for tonight — read it or listen, ready in seconds.`}
        canonical={`/bedtime-stories/${length.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: `${length.label} Stories`, path: `/bedtime-stories/${length.slug}` }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${length.emoji} ${length.label} Bedtime Stories`}
        title={`${length.label} Personalised`}
        highlight="Bedtime Stories"
        description={`${length.description} Every story stars your child by name.`}
        ctaLabel="Create a Story Free"
        visual={<div style={{ fontSize: 'clamp(80px,15vw,120px)', lineHeight: 1 }}>{length.emoji}</div>}
      />
      <HowItWorks childLabel="your child" />
      <FeatureCards />
      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            More story lengths
          </h2>
          <div className="row g-3 justify-content-center">
            {STORY_LENGTHS.filter(l => l.slug !== length.slug).map(l => (
              <div className="col-12 col-sm-4" key={l.slug}>
                <Link to={`/bedtime-stories/length/${l.slug}`}
                  className="d-block text-center p-3 rounded-4 text-decoration-none"
                  style={{ border: '2px solid #e9d5ff', background: 'var(--card-bg)' }}>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>{l.emoji}</div>
                  <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '14px' }}>{l.label} Stories</div>
                  <div style={{ color: 'var(--text-soft)', fontSize: '12px' }}>{l.minutes} minutes</div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CtaSection heading="Find the perfect length for bedtime tonight" sub="Short, long, or just right — every story personalised for your child." />
    </>
  );
}

// ── GENDER PAGE ───────────────────────────────────────────────────────────────
function GenderPage({ gender }) {
  const other = GENDERS.find(g => g.slug !== gender.slug);
  return (
    <>
      <SEO
        title={`${gender.title} — Free, Personalised & Magical`}
        description={`${gender.description} Try free today — 5 stories every month at no cost.`}
        canonical={`/bedtime-stories/${gender.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: gender.title, path: `/bedtime-stories/${gender.slug}` }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${gender.emoji} ${gender.title}`}
        title={gender.title.replace('Bedtime Stories for ', 'Personalised Bedtime Stories for ')}
        highlight=""
        description={gender.description}
        ctaLabel="Create a Story Free"
        visual={<div style={{ fontSize: 'clamp(80px,15vw,120px)', lineHeight: 1 }}>{gender.emoji}</div>}
      />
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(22px,4vw,34px)' }}>
            Popular themes
          </h2>
          <div className="row g-3 justify-content-center">
            {gender.themes.map(t => {
              const theme = THEMES.find(th => th.label === t);
              if (!theme) return null;
              return (
                <div className="col-6 col-sm-4 col-md-2" key={t}>
                  <Link to={`/bedtime-stories/themes/${theme.slug}`}
                    className="d-block text-center p-3 rounded-4 text-decoration-none"
                    style={{ border: '2px solid #ede9fe', background: 'var(--page-bg)' }}>
                    <div style={{ fontSize: '28px', marginBottom: '6px' }}>{theme.emoji}</div>
                    <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '13px' }}>{theme.label}</div>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <HowItWorks childLabel="your child" />
      <FeatureCards />
      {other && (
        <section className="py-4 bg-white border-top">
          <div className="container text-center py-2">
            <p className="text-muted mb-2" style={{ fontSize: '15px' }}>Also looking for:</p>
            <Link to={`/bedtime-stories/gender/${other.slug}`} className="btn btn-outline-primary rounded-pill px-4">
              {other.emoji} {other.title}
            </Link>
          </div>
        </section>
      )}
      <CtaSection heading={`Create your ${gender.label === 'Girls' ? 'daughter' : 'son'}'s story tonight`} sub="Personalised just for them — ready in 15 seconds." />
    </>
  );
}

// ── RELATIONSHIP PAGE ─────────────────────────────────────────────────────────
function RelationshipPage({ rel }) {
  const others = RELATIONSHIPS.filter(r => r.slug !== rel.slug);
  return (
    <>
      <SEO
        title={`Bedtime Stories ${rel.label} — Personalised with Voice Cloning`}
        description={`${rel.description} Clone your voice once and be there for bedtime every night.`}
        canonical={`/bedtime-stories/${rel.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: `Stories ${rel.label}`, path: `/bedtime-stories/${rel.slug}` }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${rel.emoji} ${rel.label}`}
        title="Bedtime Stories"
        highlight={rel.label}
        description={`${rel.description}`}
        ctaLabel="Try Free Tonight"
        visual={<div style={{ fontSize: 'clamp(80px,15vw,120px)', lineHeight: 1 }}>{rel.emoji}</div>}
      />
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <div className="row g-4 align-items-center">
            <div className="col-12 col-lg-6">
              <h2 className="fw-bold mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(22px,4vw,34px)' }}>
                How voice cloning works
              </h2>
              {[
                'Record your voice reading a short passage — takes 2 minutes.',
                'We clone your voice using advanced AI.',
                'Every bedtime story is narrated in your voice — forever.',
                'Your child hears YOU — even when you\'re not there.',
              ].map((step, i) => (
                <div key={i} className="d-flex gap-3 mb-3">
                  <div className="flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: '30px', height: '30px', background: '#ede9fe', color: '#7c3aed', fontWeight: 700, fontSize: '13px' }}>
                    {i + 1}
                  </div>
                  <p className="text-muted mb-0" style={{ fontSize: '14px', lineHeight: 1.7 }}>{step}</p>
                </div>
              ))}
            </div>
            <div className="col-12 col-lg-6">
              <div className="p-3 p-md-4 rounded-4" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>{rel.emoji}</div>
                <h5 className="fw-bold mb-3" style={{ color: 'var(--text-main)', fontSize: '15px' }}>Why this matters</h5>
                <p className="text-muted mb-0" style={{ fontSize: '14px', lineHeight: 1.7 }}>
                  {rel.description} Distance should never get in the way of a magical bedtime moment.
                  With Dreamy Bedtime, it never will.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <HowItWorks childLabel="your child" />
      <FeatureCards />
      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            Stories for every family member
          </h2>
          <div className="row g-3 justify-content-center">
            {others.map(r => (
              <div className="col-6 col-md-4" key={r.slug}>
                <Link to={`/bedtime-stories/from/${r.slug}`}
                  className="d-block text-center p-3 rounded-4 text-decoration-none"
                  style={{ border: '2px solid #e9d5ff', background: 'var(--card-bg)' }}>
                  <div style={{ fontSize: '26px', marginBottom: '6px' }}>{r.emoji}</div>
                  <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '13px' }}>{r.label}</div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CtaSection heading="Be there for bedtime — every night" sub="Clone your voice once. Tell bedtime stories forever." />
    </>
  );
}

// ── COUNTRY PAGE ──────────────────────────────────────────────────────────────
function CountryPage({ country }) {
  const others = COUNTRIES.filter(c => c.slug !== country.slug);
  return (
    <>
      <SEO
        title={`Bedtime Stories for Kids in ${country.label} — Free & Personalised`}
        description={`${country.description} Try free — 5 stories every month at no cost.`}
        canonical={`/bedtime-stories/${country.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: `Stories for ${country.label}`, path: `/bedtime-stories/${country.slug}` }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${country.emoji} For Families in ${country.label}`}
        title="Personalised Bedtime Stories for"
        highlight={country.label}
        description={country.description}
        ctaLabel="Start Free Tonight"
        visual={<div style={{ fontSize: 'clamp(70px,14vw,110px)', lineHeight: 1 }}>{country.emoji}</div>}
      />
      <HowItWorks childLabel="your child" />
      <FeatureCards />
      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            Loved by families worldwide
          </h2>
          <div className="row g-2 justify-content-center">
            {others.map(c => (
              <div className="col-6 col-sm-4 col-md-2" key={c.slug}>
                <Link to={`/bedtime-stories/country/${c.slug}`}
                  className="d-block text-center p-2 p-md-3 rounded-4 text-decoration-none"
                  style={{ border: '2px solid #e9d5ff', background: 'var(--card-bg)' }}>
                  <div style={{ fontSize: '28px', marginBottom: '6px' }}>{c.emoji}</div>
                  <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '12px' }}>{c.label}</div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CtaSection heading={`Join families in ${country.label} making bedtime magical`} sub="Personalised stories — ready in 15 seconds, loved every night." />
    </>
  );
}

// ── HOLIDAY PAGE ──────────────────────────────────────────────────────────────
function HolidayPage({ holiday }) {
  const others = HOLIDAYS.filter(h => h.slug !== holiday.slug);
  return (
    <>
      <SEO
        title={`${holiday.label} Bedtime Stories for Kids — Free & Personalised`}
        description={`Free personalised ${holiday.label} bedtime stories starring your child by name. Make the holiday magical this year — read it or listen, ready in about 15 seconds.`}
        canonical={`/bedtime-stories/holidays/${holiday.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: `${holiday.label} Stories`, path: `/bedtime-stories/holidays/${holiday.slug}` }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${holiday.emoji} ${holiday.label} Stories`}
        title="Personalised"
        highlight={`${holiday.label} Bedtime Stories`}
        description={`${holiday.description} Every story stars your child by name — making this holiday season truly unforgettable.`}
        ctaLabel={`Create a ${holiday.label} Story Free`}
        visual={<div style={{ fontSize: 'clamp(80px,15vw,120px)', lineHeight: 1 }}>{holiday.emoji}</div>}
      />
      <HowItWorks childLabel="your child" />
      <FeatureCards />
      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            More seasonal stories
          </h2>
          <div className="row g-2 g-md-3 justify-content-center">
            {others.map(h => (
              <div className="col-6 col-sm-4 col-md-2" key={h.slug}>
                <Link to={`/bedtime-stories/holidays/${h.slug}`}
                  className="d-block text-center p-2 p-md-3 rounded-4 text-decoration-none"
                  style={{ border: '2px solid #e9d5ff', background: 'var(--card-bg)' }}>
                  <div style={{ fontSize: '28px', marginBottom: '6px' }}>{h.emoji}</div>
                  <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '12px' }}>{h.label}</div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CtaSection
        heading={`Create your child's ${holiday.label} story tonight`}
        sub="Make this holiday season magical with a story starring your child."
      />
    </>
  );
}

// ── SIBLINGS PAGE ─────────────────────────────────────────────────────────────
function SiblingsPage({ sibling }) {
  const others = SIBLINGS.filter(s => s.slug !== sibling.slug);
  return (
    <>
      <SEO
        title={`Bedtime Stories for ${sibling.label} — Both Children Star by Name`}
        description={`${sibling.description} Created in seconds — every child feels equally special.`}
        canonical={`/bedtime-stories/${sibling.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: `Stories for ${sibling.label}`, path: `/bedtime-stories/${sibling.slug}` }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${sibling.emoji} ${sibling.label}`}
        title="Personalised Bedtime Stories for"
        highlight={sibling.label}
        description={`${sibling.description}`}
        ctaLabel="Create a Story Free"
        visual={<div style={{ fontSize: 'clamp(70px,14vw,110px)', lineHeight: 1 }}>{sibling.emoji}</div>}
      />
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(22px,4vw,34px)' }}>
            How multi-child stories work
          </h2>
          <div className="row g-3 justify-content-center">
            {[
              { icon: '👶', title: 'Add all your children',  body: 'Enter both names, ages, and their individual interests and personalities.' },
              { icon: '✨', title: 'One shared adventure',   body: 'We craft a story where both children are heroes — each with their own role in the adventure.' },
              { icon: '🌙', title: 'Both feel equally special', body: 'No child is left out. Both names appear throughout — making bedtime fair and magical for everyone.' },
            ].map(f => (
              <div className="col-12 col-md-4" key={f.title}>
                <div className="text-center p-3 p-md-4 rounded-4 h-100" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>{f.icon}</div>
                  <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{f.title}</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <FeatureCards />
      {others.length > 0 && (
        <section className="py-4" style={{ background: 'var(--page-bg)' }}>
          <div className="container text-center py-2">
            <div className="d-flex flex-wrap gap-3 justify-content-center">
              {others.map(s => (
                <Link key={s.slug} to={`/bedtime-stories/siblings/${s.slug}`}
                  className="btn rounded-pill"
                  style={{ border: '2px solid #e9d5ff', color: 'var(--text-main)', background: 'var(--card-bg)', fontWeight: 600 }}>
                  {s.emoji} {s.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      <CtaSection heading="Create a story for all your children tonight" sub="Every child stars as a hero — in one shared magical adventure." />
    </>
  );
}

// ── GIFT PAGE ─────────────────────────────────────────────────────────────────
function GiftPage({ gift }) {
  const others = GIFTS.filter(g => g.slug !== gift.slug);
  return (
    <>
      <SEO
        title={`${gift.label} — Personalised Bedtime Story Subscription`}
        description={`${gift.description} The most unique gift for any child — stories they'll love every single night.`}
        canonical={`/bedtime-stories/${gift.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: gift.label, path: `/bedtime-stories/${gift.slug}` }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${gift.emoji} ${gift.label}`}
        title="The Most Unique"
        highlight={gift.label}
        description={`${gift.description}`}
        ctaLabel="Give the Gift of Stories"
        ctaTo="/gift"
        visual={<div style={{ fontSize: 'clamp(80px,15vw,120px)', lineHeight: 1 }}>{gift.emoji}</div>}
      />
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(22px,4vw,34px)' }}>
            Why Dreamy Bedtime is the perfect gift
          </h2>
          <div className="row g-3 justify-content-center">
            {[
              { icon: '🌙', title: 'Used every single night', body: 'Unlike toys that get forgotten, bedtime stories are part of the daily routine — your gift is enjoyed every night.' },
              { icon: '✨', title: 'Completely unique',        body: 'No other child has this story. Every adventure is created just for them — their name, their world, their hero.' },
              { icon: '🎁', title: 'Instant delivery',        body: 'Gift a subscription instantly via email. The recipient creates their account and starts generating stories right away.' },
            ].map(f => (
              <div className="col-12 col-md-4" key={f.title}>
                <div className="text-center p-3 p-md-4 rounded-4 h-100" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>{f.icon}</div>
                  <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{f.title}</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <FeatureCards />
      {others.length > 0 && (
        <section className="py-4" style={{ background: 'var(--page-bg)' }}>
          <div className="container text-center py-2">
            <div className="d-flex flex-wrap gap-3 justify-content-center">
              {others.map(g => (
                <Link key={g.slug} to={`/bedtime-stories/gifts/${g.slug}`}
                  className="btn rounded-pill"
                  style={{ border: '2px solid #e9d5ff', color: 'var(--text-main)', background: 'var(--card-bg)', fontWeight: 600 }}>
                  {g.emoji} {g.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      <CtaSection heading="Give the gift of magical bedtimes" sub="A personalised story subscription — the gift they'll love every single night." />
    </>
  );
}

// ── COMPARISON PAGE ───────────────────────────────────────────────────────────
function ComparisonPage({ comp }) {
  return (
    <>
      <SEO
        title={`${comp.label} — Free Personalised Bedtime Stories`}
        description={`${comp.description} Try free today — 5 stories every month at no cost.`}
        canonical={`/bedtime-stories/${comp.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: comp.label, path: `/bedtime-stories/${comp.slug}` }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${comp.emoji} ${comp.label}`}
        title={comp.label}
        highlight=""
        description={comp.description}
        ctaLabel="Try Free — 5 Stories/Month"
        visual={<div style={{ fontSize: 'clamp(80px,15vw,120px)', lineHeight: 1 }}>{comp.emoji}</div>}
      />
      <HowItWorks childLabel="your child" />
      <FeatureCards />
      <CtaSection heading="Start creating personalised stories tonight" sub="Free to start. No credit card required. Stories in 15 seconds." />
    </>
  );
}

// ── BILINGUAL PAGE ────────────────────────────────────────────────────────────
function BilingualPage({ bili }) {
  const others = BILINGUAL.filter(b => b.slug !== bili.slug);
  return (
    <>
      <SEO
        title={`${bili.label} Bedtime Stories — Personalised for Bilingual Families`}
        description={`${bili.description} Try free today.`}
        canonical={`/bedtime-stories/bilingual/${bili.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: `${bili.label} Stories`, path: `/bedtime-stories/bilingual/${bili.slug}` }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${bili.emoji} ${bili.label}`}
        title="Bilingual Bedtime Stories —"
        highlight={bili.label}
        description={bili.description}
        ctaLabel="Create a Story Free"
        visual={<div style={{ fontSize: 'clamp(60px,12vw,90px)', lineHeight: 1.2 }}>{bili.emoji}</div>}
      />
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(22px,4vw,34px)' }}>
            Why bilingual bedtime stories matter
          </h2>
          <div className="row g-3 justify-content-center">
            {[
              { icon: '🧠', title: 'Language retention',   body: 'Children who hear bedtime stories in their heritage language retain it far better than classroom learning alone.' },
              { icon: '🌍', title: 'Cultural connection',  body: 'Stories in your native language keep children connected to their culture, family, and roots.' },
              { icon: '💡', title: 'Cognitive benefits',   body: 'Bilingual children develop stronger problem-solving skills, creativity, and mental flexibility.' },
            ].map(f => (
              <div className="col-12 col-md-4" key={f.title}>
                <div className="text-center p-3 p-md-4 rounded-4 h-100" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>{f.icon}</div>
                  <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{f.title}</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <HowItWorks childLabel="your child" />
      <FeatureCards />
      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            More bilingual options
          </h2>
          <div className="row g-2 g-md-3 justify-content-center">
            {others.map(b => (
              <div className="col-6 col-md-4" key={b.slug}>
                <Link to={`/bedtime-stories/bilingual/${b.slug}`}
                  className="d-block text-center p-3 rounded-4 text-decoration-none"
                  style={{ border: '2px solid #e9d5ff', background: 'var(--card-bg)' }}>
                  <div style={{ fontSize: '22px', marginBottom: '4px' }}>{b.emoji}</div>
                  <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '12px' }}>{b.label}</div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CtaSection heading="Raise a bilingual child through the magic of stories" sub="Switch languages any night — every story personalised for your child." />
    </>
  );
}

// ── FREE PAGE ─────────────────────────────────────────────────────────────────
function FreePage() {
  return (
    <>
      <SEO
        title="Free Personalised Bedtime Stories for Kids — 5 Free Stories Every Month"
        description="Get 5 free personalised bedtime stories every month — no credit card required. Stories starring your child by name, generated in 15 seconds. Try free today."
        canonical="/bedtime-stories/free"
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Free Bedtime Stories', path: '/bedtime-stories/free' }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <section className="bg-dream text-white" style={{ padding: '60px 0 50px' }}>
        <div className="container text-center px-4 py-2">
          <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill" style={{ fontSize: '12px' }}>🎁 No credit card required</span>
          <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(28px,5vw,52px)' }}>
            Free Personalised Bedtime Stories — <span style={{ color: '#f9c74f' }}>Every Month</span>
          </h1>
          <p className="mb-4 mx-auto" style={{ color: '#c9a8ff', fontSize: 'clamp(15px,2vw,18px)', maxWidth: '560px' }}>
            Get 5 personalised bedtime stories every month — completely free. No credit card. No catch. Just magical stories starring your child.
          </p>
          <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center align-items-center">
            <Link to="/register" className="btn btn-warning btn-lg rounded-pill fw-bold" style={{ fontSize: '16px' }}>
              Start Free — No Card Required
            </Link>
          </div>
        </div>
      </section>

      <StoryPreviewWidget />
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(22px,4vw,34px)' }}>
            What you get for free
          </h2>
          <div className="row g-3 justify-content-center">
            {[
              { icon: '📖', title: '5 stories per month',       body: 'Generate 5 unique, personalised bedtime stories every month — completely free, forever.' },
              { icon: '✨', title: 'Fully personalised',          body: "Every story stars your child by name with their favourite interests — not a generic template." },
              { icon: '🖼️', title: 'Beautiful illustrations',     body: 'Each story comes with a custom illustration — no extra cost on the free plan.' },
              { icon: '🌍', title: 'All languages',              body: 'Access all 11 supported languages on the free plan. Stories in Hindi, Spanish, French and more.' },
              { icon: '📚', title: 'Save your stories',          body: 'All your generated stories are saved in your library — read them again and again.' },
              { icon: '💳', title: 'No credit card ever needed', body: "Sign up with just your email. We'll never ask for payment details to access the free plan." },
            ].map(f => (
              <div className="col-12 col-sm-6 col-lg-4" key={f.title}>
                <div className="h-100 p-3 p-md-4 rounded-4 border" style={{ borderColor: '#e9d5ff' }}>
                  <div style={{ fontSize: '28px', marginBottom: '10px' }}>{f.icon}</div>
                  <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{f.title}</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '14px', lineHeight: 1.6 }}>{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <HowItWorks childLabel="your child" />
      <CtaSection heading="Start your free stories tonight" sub="5 free personalised bedtime stories every month — no card needed." />
    </>
  );
}

// ── ANIMAL PAGE ───────────────────────────────────────────────────────────────
function AnimalPage({ animal }) {
  const otherAnimals = ANIMALS.filter(a => a.slug !== animal.slug).slice(0, 12);
  return (
    <>
      <SEO
        title={`${animal.label} Bedtime Stories for Kids — Free & Personalised`}
        description={`Free personalised ${animal.label.toLowerCase()} bedtime stories starring your child as the hero. A cuddly new adventure every night — read it or listen, ready in seconds.`}
        canonical={`/bedtime-stories/animals/${animal.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Animal Stories', path: '/bedtime-stories/animals' },
          { name: `${animal.label} Stories`, path: `/bedtime-stories/animals/${animal.slug}` },
        ]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${animal.emoji} ${animal.label} Bedtime Stories`}
        title="Personalised"
        highlight={`${animal.label} Bedtime Stories`}
        description={`${animal.description} Every story stars your child by name — created in seconds, loved forever.`}
        ctaLabel={`Create a ${animal.label} Story Free`}
        visual={<div style={{ fontSize: 'clamp(80px,15vw,120px)', lineHeight: 1 }}>{animal.emoji}</div>}
        preview={{ initialInterests: ['animals'], heading: `See a ${animal.label.toLowerCase()} story come to life`, subheading: `Type your child's name and watch a ${animal.label.toLowerCase()} bedtime story appear — free, no signup needed.` }}
      />
      <HowItWorks childLabel="your child" />

      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            Why children love {animal.label.toLowerCase()} stories
          </h2>
          <p className="text-muted mb-4" style={{ fontSize: '15px', lineHeight: 1.8, maxWidth: '720px' }}>
            {animal.whyKidsLove}
          </p>
          <h3 className="fw-bold mb-3" style={{ color: 'var(--text-main)', fontSize: 'clamp(16px,3vw,22px)' }}>
            Popular {animal.label.toLowerCase()} story ideas
          </h3>
          <div className="row g-3">
            {animal.storyTitles.map((title, i) => (
              <div className="col-12 col-md-6" key={i}>
                <div className="d-flex align-items-center gap-3 p-3 rounded-4" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                  <span style={{ fontSize: '22px', flexShrink: 0 }}>{animal.emoji}</span>
                  <span style={{ color: 'var(--text-main)', fontWeight: 600, fontSize: '14px', lineHeight: 1.4 }}>{title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold mb-4 text-center" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(18px,3vw,26px)' }}>
            Amazing {animal.label.toLowerCase()} facts for curious kids
          </h2>
          <div className="row g-3 justify-content-center">
            {animal.facts.map((fact, i) => (
              <div className="col-12 col-md-4" key={i}>
                <div className="p-3 p-md-4 rounded-4 h-100 bg-white" style={{ border: '1px solid #e9d5ff' }}>
                  <div className="mb-2" style={{ fontSize: '20px' }}>💡</div>
                  <p className="mb-0" style={{ color: 'var(--text-body)', fontSize: '14px', lineHeight: 1.7 }}>{fact}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FeatureCards />

      <section className="py-5 bg-white">
        <div className="container py-2">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            More animal bedtime stories
          </h2>
          <div className="row g-2 g-md-3 justify-content-center">
            {otherAnimals.map(a => (
              <div className="col-4 col-sm-3 col-md-2" key={a.slug}>
                <Link to={`/bedtime-stories/animals/${a.slug}`}
                  className="d-block text-center p-2 p-md-3 rounded-4 text-decoration-none h-100"
                  style={{ border: '2px solid #ede9fe' }}>
                  <div style={{ fontSize: '28px', marginBottom: '6px' }}>{a.emoji}</div>
                  <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '12px' }}>{a.label}</div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        heading={`Ready to create your child's ${animal.label.toLowerCase()} adventure?`}
        sub="Join thousands of families making bedtime magical every night."
      />
    </>
  );
}

function AnimalsIndexPage() {
  return (
    <>
      <SEO
        title="Animal Bedtime Stories — Bears, Lions, Dolphins & More — Personalised"
        description="Create personalised animal bedtime stories — bears, bunnies, lions, elephants, dolphins and more. Every story stars your child by name. Try free."
        canonical="/bedtime-stories/animals"
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Animal Stories', path: '/bedtime-stories/animals' }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <section className="bg-dream text-white" style={{ padding: '60px 0 50px' }}>
        <div className="container text-center px-4 py-2">
          <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(28px,5vw,50px)' }}>
            Animal <span style={{ color: '#f9c74f' }}>Bedtime Stories</span>
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: 'clamp(15px,2vw,18px)', maxWidth: '520px', margin: '0 auto' }}>
            Pick your child's favourite animal and we'll create a personalised story starring them.
          </p>
        </div>
      </section>

      <StoryPreviewWidget />
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <div className="row g-3">
            {ANIMALS.map(a => (
              <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={a.slug}>
                <Link to={`/bedtime-stories/animals/${a.slug}`}
                  className="d-block p-3 rounded-4 text-decoration-none h-100"
                  style={{ border: '2px solid #ede9fe' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>{a.emoji}</div>
                  <h5 className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontSize: '13px' }}>{a.label}</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '11px', lineHeight: 1.4 }}>{a.description}</p>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CtaSection heading="Create your child's animal adventure tonight" sub="Choose any animal and we'll personalise a magical story just for them." />
    </>
  );
}

// ── SETTING PAGE ──────────────────────────────────────────────────────────────
function SettingPage({ setting }) {
  const otherSettings = SETTINGS.filter(s => s.slug !== setting.slug).slice(0, 8);
  const bodyText = setting.whyItsMagical || setting.whyKidsLove || '';
  return (
    <>
      <SEO
        title={`${setting.label} Bedtime Stories for Kids — Free & Personalised`}
        description={`Free personalised bedtime stories set in a ${setting.label.toLowerCase()}, starring your child as the hero. A magical new adventure nightly — read or listen in seconds.`}
        canonical={`/bedtime-stories/settings/${setting.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Story Settings', path: '/bedtime-stories/settings' },
          { name: `${setting.label} Stories`, path: `/bedtime-stories/settings/${setting.slug}` },
        ]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${setting.emoji} ${setting.label} Stories`}
        title="Bedtime Stories Set in a"
        highlight={setting.label}
        description={`${setting.description} Every story stars your child by name — created in seconds.`}
        ctaLabel={`Create a ${setting.label} Story Free`}
        visual={<div style={{ fontSize: 'clamp(80px,15vw,120px)', lineHeight: 1 }}>{setting.emoji}</div>}
      />
      <HowItWorks childLabel="your child" />

      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            Why the {setting.label.toLowerCase()} makes such a magical story world
          </h2>
          <p className="text-muted mb-4" style={{ fontSize: '15px', lineHeight: 1.8, maxWidth: '720px' }}>
            {bodyText}
          </p>
          <h3 className="fw-bold mb-3" style={{ color: 'var(--text-main)', fontSize: 'clamp(16px,3vw,22px)' }}>
            Story ideas set in a {setting.label.toLowerCase()}
          </h3>
          <div className="row g-3">
            {setting.storyTitles.map((title, i) => (
              <div className="col-12 col-md-6" key={i}>
                <div className="d-flex align-items-center gap-3 p-3 rounded-4" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                  <span style={{ fontSize: '22px', flexShrink: 0 }}>{setting.emoji}</span>
                  <span style={{ color: 'var(--text-main)', fontWeight: 600, fontSize: '14px', lineHeight: 1.4 }}>{title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold mb-4 text-center" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(18px,3vw,26px)' }}>
            Fascinating facts about {setting.label.toLowerCase()}s
          </h2>
          <div className="row g-3 justify-content-center">
            {setting.facts.map((fact, i) => (
              <div className="col-12 col-md-4" key={i}>
                <div className="p-3 p-md-4 rounded-4 h-100 bg-white" style={{ border: '1px solid #e9d5ff' }}>
                  <div className="mb-2" style={{ fontSize: '20px' }}>💡</div>
                  <p className="mb-0" style={{ color: 'var(--text-body)', fontSize: '14px', lineHeight: 1.7 }}>{fact}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FeatureCards />

      <section className="py-5 bg-white">
        <div className="container py-2">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            Explore more story worlds
          </h2>
          <div className="row g-2 g-md-3 justify-content-center">
            {otherSettings.map(s => (
              <div className="col-4 col-sm-3 col-md-2" key={s.slug}>
                <Link to={`/bedtime-stories/settings/${s.slug}`}
                  className="d-block text-center p-2 p-md-3 rounded-4 text-decoration-none h-100"
                  style={{ border: '2px solid #ede9fe' }}>
                  <div style={{ fontSize: '28px', marginBottom: '6px' }}>{s.emoji}</div>
                  <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '12px' }}>{s.label}</div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        heading={`Send your child on a ${setting.label.toLowerCase()} adventure tonight`}
        sub="Personalised for your child — ready in 15 seconds, loved forever."
      />
    </>
  );
}

function SettingsIndexPage() {
  return (
    <>
      <SEO
        title="Bedtime Stories by Setting — Enchanted Forest, Castle, Space & More"
        description="Create personalised bedtime stories set in magical worlds — enchanted forests, castles, underwater kingdoms, space stations and more. Try free."
        canonical="/bedtime-stories/settings"
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Story Settings', path: '/bedtime-stories/settings' }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <section className="bg-dream text-white" style={{ padding: '60px 0 50px' }}>
        <div className="container text-center px-4 py-2">
          <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(28px,5vw,50px)' }}>
            Bedtime Stories Set in <span style={{ color: '#f9c74f' }}>Magical Worlds</span>
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: 'clamp(15px,2vw,18px)', maxWidth: '520px', margin: '0 auto' }}>
            Choose a magical world and we'll create a personalised story where your child is the hero.
          </p>
        </div>
      </section>

      <StoryPreviewWidget />
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <div className="row g-3">
            {SETTINGS.map(s => (
              <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={s.slug}>
                <Link to={`/bedtime-stories/settings/${s.slug}`}
                  className="d-block p-3 rounded-4 text-decoration-none h-100"
                  style={{ border: '2px solid #ede9fe' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>{s.emoji}</div>
                  <h5 className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontSize: '13px' }}>{s.label}</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '11px', lineHeight: 1.4 }}>{s.description}</p>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CtaSection heading="Take your child to a magical world tonight" sub="Pick a setting — we'll create a story your child will never forget." />
    </>
  );
}

// ── MOOD PAGE ─────────────────────────────────────────────────────────────────
function MoodPage({ mood }) {
  const otherMoods = MOODS.filter(m => m.slug !== mood.slug);
  return (
    <>
      <SEO
        title={`${mood.label} Bedtime Stories for Kids — Free & Personalised`}
        description={`Free personalised ${mood.label.toLowerCase()} bedtime stories starring your child by name. Set exactly the right mood for tonight — read it or listen, ready in seconds.`}
        canonical={`/bedtime-stories/mood/${mood.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Story Mood', path: '/bedtime-stories/mood' },
          { name: `${mood.label} Stories`, path: `/bedtime-stories/mood/${mood.slug}` },
        ]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${mood.emoji} ${mood.label} Bedtime Stories`}
        title={`${mood.label}`}
        highlight="Bedtime Stories"
        description={`${mood.description} Every story is personalised with your child's name and interests.`}
        ctaLabel={`Create a ${mood.label} Story Free`}
        visual={<div style={{ fontSize: 'clamp(80px,15vw,120px)', lineHeight: 1 }}>{mood.emoji}</div>}
      />

      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            Why {mood.label.toLowerCase()} stories at bedtime
          </h2>
          <p className="text-muted mb-4" style={{ fontSize: '15px', lineHeight: 1.8, maxWidth: '720px' }}>
            {mood.whyItHelps}
          </p>

          {mood.bestFor && (
            <div className="p-3 p-md-4 rounded-4 mb-4" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
              <h5 className="fw-bold mb-3" style={{ color: 'var(--text-main)', fontSize: '15px' }}>
                {mood.emoji} Perfect for:
              </h5>
              {mood.bestFor.map((item, i) => (
                <div key={i} className="d-flex align-items-start gap-2 mb-2">
                  <i className="bi bi-check-circle-fill mt-1 flex-shrink-0" style={{ color: '#7c3aed' }}></i>
                  <span style={{ color: 'var(--text-body)', fontSize: '14px' }}>{item}</span>
                </div>
              ))}
            </div>
          )}

          <h3 className="fw-bold mb-3" style={{ color: 'var(--text-main)', fontSize: 'clamp(16px,3vw,22px)' }}>
            {mood.label} story ideas for tonight
          </h3>
          <div className="row g-3">
            {mood.storyTitles.map((title, i) => (
              <div className="col-12 col-md-6" key={i}>
                <div className="d-flex align-items-center gap-3 p-3 rounded-4" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                  <span style={{ fontSize: '22px', flexShrink: 0 }}>{mood.emoji}</span>
                  <span style={{ color: 'var(--text-main)', fontWeight: 600, fontSize: '14px', lineHeight: 1.4 }}>{title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HowItWorks childLabel="your child" />
      <FeatureCards />

      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            More story moods to explore
          </h2>
          <div className="row g-2 g-md-3 justify-content-center">
            {otherMoods.map(m => (
              <div className="col-6 col-sm-4 col-md-3 col-lg-2" key={m.slug}>
                <Link to={`/bedtime-stories/mood/${m.slug}`}
                  className="d-block text-center p-2 p-md-3 rounded-4 text-decoration-none h-100"
                  style={{ border: '2px solid #e9d5ff', background: 'var(--card-bg)' }}>
                  <div style={{ fontSize: '26px', marginBottom: '6px' }}>{m.emoji}</div>
                  <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '12px' }}>{m.label}</div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        heading={`Create your child's ${mood.label.toLowerCase()} bedtime story tonight`}
        sub="Personalised for your child — the exact mood you need, ready in seconds."
      />
    </>
  );
}

function MoodsIndexPage() {
  return (
    <>
      <SEO
        title="Bedtime Stories by Mood — Calming, Funny, Exciting, Heartwarming & More"
        description="Choose the perfect mood for tonight's bedtime story — calming, funny, exciting, heartwarming, spooky or silly. Every story personalised for your child."
        canonical="/bedtime-stories/mood"
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Story Mood', path: '/bedtime-stories/mood' }]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <section className="bg-dream text-white" style={{ padding: '60px 0 50px' }}>
        <div className="container text-center px-4 py-2">
          <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", fontSize: 'clamp(28px,5vw,50px)' }}>
            Bedtime Stories for <span style={{ color: '#f9c74f' }}>Every Mood</span>
          </h1>
          <p style={{ color: '#c9a8ff', fontSize: 'clamp(15px,2vw,18px)', maxWidth: '520px', margin: '0 auto' }}>
            Pick the perfect tone for tonight — every story personalised for your child.
          </p>
        </div>
      </section>

      <StoryPreviewWidget />
      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <div className="row g-3">
            {MOODS.map(m => (
              <div className="col-6 col-sm-4 col-md-3" key={m.slug}>
                <Link to={`/bedtime-stories/mood/${m.slug}`}
                  className="d-block p-3 rounded-4 text-decoration-none h-100"
                  style={{ border: '2px solid #ede9fe' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>{m.emoji}</div>
                  <h5 className="fw-bold mb-1" style={{ color: 'var(--text-main)', fontSize: '14px' }}>{m.label}</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '11px', lineHeight: 1.4 }}>{m.description}</p>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      <CtaSection heading="Choose tonight's mood and create the perfect story" sub="Every story personalised for your child — ready in 15 seconds." />
    </>
  );
}

// ── NAME + THEME PAGE ─────────────────────────────────────────────────────────
function NameThemePage({ name, theme }) {
  const titleOffset = name.charCodeAt(0) % NAME_STORY_TITLE_TEMPLATES.length;
  const genericTitles = [0, 1, 2, 3].map(i =>
    NAME_STORY_TITLE_TEMPLATES[(titleOffset + i) % NAME_STORY_TITLE_TEMPLATES.length].replace(/\{name\}/g, name)
  );
  const otherThemes = THEMES.filter(t => t.slug !== theme.slug).slice(0, 8);
  const otherNames = POPULAR_NAMES.filter(n => n.toLowerCase() !== name.toLowerCase()).slice(0, 16);

  return (
    <>
      <SEO
        title={`${name}'s ${theme.label} Bedtime Story — Free & Personalised`}
        description={`A free, personalised ${theme.label.toLowerCase()} bedtime story starring ${name} as the hero. Watch their eyes light up — read it or listen, ready in about 15 seconds.`}
        canonical={`/bedtime-stories/for/${name.toLowerCase()}/${theme.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Stories by Name', path: '/bedtime-stories/for' },
          { name: `Stories for ${name}`, path: `/bedtime-stories/for/${name.toLowerCase()}` },
          { name: `${name}'s ${theme.label} Story`, path: `/bedtime-stories/for/${name.toLowerCase()}/${theme.slug}` },
        ]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />

      <section className="bg-dream text-white" style={{ padding: '60px 0 50px' }}>
        <div className="container px-4">
          <div className="row align-items-center g-4">
            <div className="col-12 col-lg-7 text-center text-lg-start">
              <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill" style={{ fontSize: '12px' }}>
                {theme.emoji} {name}'s {theme.label} Adventure
              </span>
              <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", lineHeight: 1.2, fontSize: 'clamp(26px,5vw,50px)' }}>
                A <span style={{ color: '#f9c74f' }}>{theme.label}</span> Bedtime Story Starring <span style={{ color: '#f9c74f' }}>{name}</span>
              </h1>
              <p className="mb-4" style={{ fontSize: 'clamp(15px,2vw,18px)', color: '#c9a8ff', lineHeight: 1.7 }}>
                Imagine {name}'s face when they hear a {theme.label.toLowerCase()} bedtime story where <strong style={{ color: '#fff' }}>they</strong> are the hero. Their name. Their adventure. Created in 15 seconds.
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start mb-4">
                <Link to="/register" className="btn btn-warning btn-lg rounded-pill fw-bold" style={{ fontSize: '16px' }}>
                  Create {name}'s {theme.label} Story Free
                </Link>
              </div>
              <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-lg-start">
                {['No credit card required', 'Ready in 15 seconds', '5 free stories/month'].map(t => (
                  <span key={t} style={{ color: '#c9a8ff', fontSize: '12px' }}>
                    <i className="bi bi-check-circle-fill me-1" style={{ color: '#4ade80' }}></i>{t}
                  </span>
                ))}
              </div>
            </div>
            <div className="col-12 col-lg-5 text-center">
              <div style={{ fontSize: 'clamp(80px,14vw,120px)', lineHeight: 1, marginBottom: '12px' }}>{theme.emoji}</div>
              <div className="badge rounded-pill px-3 py-2" style={{ background: 'rgba(249,199,79,0.2)', color: '#f9c74f', fontSize: '14px', fontFamily: "'Fredoka One',cursive" }}>
                ⭐ {name}'s Story
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value-first: watch a real personalised story before any signup */}
      <StoryPreviewWidget
        initialName={name}
        initialInterests={previewInterestsFor(theme.label)}
        heading={`See ${name}'s ${theme.label.toLowerCase()} story come to life`}
        subheading={`Watch a magical ${theme.label.toLowerCase()} story starring ${name} appear in seconds — free, no signup needed.`}
      />

      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            Why {name} will love {theme.label.toLowerCase()} stories
          </h2>
          <p className="text-muted mb-4" style={{ fontSize: '15px', lineHeight: 1.8, maxWidth: '720px' }}>
            {theme.whyKidsLove}
          </p>
          <h3 className="fw-bold mb-3" style={{ color: 'var(--text-main)', fontSize: 'clamp(16px,3vw,22px)' }}>
            {theme.label} adventures waiting for {name} tonight
          </h3>
          <div className="row g-3">
            {genericTitles.map((title, i) => (
              <div className="col-12 col-md-6" key={i}>
                <div className="d-flex align-items-center gap-3 p-3 rounded-4" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                  <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg,#6f42c1,#9b6fe8)', color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                    {name[0]}
                  </div>
                  <span style={{ color: 'var(--text-main)', fontWeight: 600, fontSize: '14px', lineHeight: 1.4 }}>{title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold mb-4 text-center" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(18px,3vw,26px)' }}>
            {theme.label} facts {name} will love
          </h2>
          <div className="row g-3 justify-content-center">
            {theme.facts.map((fact, i) => (
              <div className="col-12 col-md-4" key={i}>
                <div className="p-3 p-md-4 rounded-4 h-100 bg-white" style={{ border: '1px solid #e9d5ff' }}>
                  <div className="mb-2" style={{ fontSize: '20px' }}>💡</div>
                  <p className="mb-0" style={{ color: 'var(--text-body)', fontSize: '14px', lineHeight: 1.7 }}>{fact}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HowItWorks childLabel={name} />
      <FeatureCards />

      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-3" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(18px,3vw,26px)' }}>
            More themes for {name}
          </h2>
          <div className="d-flex flex-wrap gap-2 justify-content-center mb-5">
            {otherThemes.map(t => (
              <Link key={t.slug} to={`/bedtime-stories/for/${name.toLowerCase()}/${t.slug}`}
                className="btn btn-sm rounded-pill text-decoration-none"
                style={{ border: '1px solid #e9d5ff', color: '#7c3aed', background: 'var(--card-bg)', fontSize: '13px' }}>
                {t.emoji} {t.label}
              </Link>
            ))}
          </div>
          <h2 className="fw-bold text-center mb-3" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(18px,3vw,26px)' }}>
            More children's {theme.label} stories
          </h2>
          <div className="d-flex flex-wrap gap-2 justify-content-center">
            {otherNames.map(n => (
              <Link key={n} to={`/bedtime-stories/for/${n.toLowerCase()}/${theme.slug}`}
                className="btn btn-sm rounded-pill text-decoration-none"
                style={{ border: '1px solid #e9d5ff', color: '#7c3aed', background: 'var(--card-bg)', fontSize: '13px' }}>
                {n}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        heading={`Create ${name}'s ${theme.label} adventure tonight`}
        sub={`A personalised ${theme.label.toLowerCase()} story starring ${name} — ready in 15 seconds, free to start.`}
      />
    </>
  );
}

// ── NAME + AGE PAGE ───────────────────────────────────────────────────────────
function NameAgePage({ name, ageGroup }) {
  const otherAges = AGE_GROUPS.filter(a => a.slug !== ageGroup.slug);
  const titleOffset = name.charCodeAt(0) % NAME_STORY_TITLE_TEMPLATES.length;
  const storyTitles = [0, 1, 2, 3].map(i =>
    NAME_STORY_TITLE_TEMPLATES[(titleOffset + i) % NAME_STORY_TITLE_TEMPLATES.length].replace(/\{name\}/g, name)
  );

  return (
    <>
      <SEO
        title={`${name}'s Bedtime Stories, Age ${ageGroup.range} — Free & Personalised`}
        description={`Free personalised bedtime stories for ${name}, just right for age ${ageGroup.range}. ${name} stars as the hero of every tale — read or listen, ready in 15 seconds.`}
        canonical={`/bedtime-stories/for/${name.toLowerCase()}/age/${ageGroup.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Stories by Name', path: '/bedtime-stories/for' },
          { name: `Stories for ${name}`, path: `/bedtime-stories/for/${name.toLowerCase()}` },
          { name: `${name} Age ${ageGroup.range}`, path: `/bedtime-stories/for/${name.toLowerCase()}/age/${ageGroup.slug}` },
        ]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />

      <section className="bg-dream text-white" style={{ padding: '60px 0 50px' }}>
        <div className="container px-4">
          <div className="row align-items-center g-4">
            <div className="col-12 col-lg-7 text-center text-lg-start">
              <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill" style={{ fontSize: '12px' }}>
                {ageGroup.emoji} Stories for {name} · Ages {ageGroup.range}
              </span>
              <h1 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", lineHeight: 1.2, fontSize: 'clamp(26px,5vw,50px)' }}>
                Personalised Bedtime Stories for <span style={{ color: '#f9c74f' }}>{name}</span> Aged <span style={{ color: '#f9c74f' }}>{ageGroup.range}</span>
              </h1>
              <p className="mb-4" style={{ fontSize: 'clamp(15px,2vw,18px)', color: '#c9a8ff', lineHeight: 1.7 }}>
                {ageGroup.description} Every story is written with {name} as the hero — perfectly matched to where they are right now.
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center justify-content-lg-start mb-4">
                <Link to="/register" className="btn btn-warning btn-lg rounded-pill fw-bold" style={{ fontSize: '16px' }}>
                  Create {name}'s Story Free
                </Link>
              </div>
              <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-lg-start">
                {['No credit card required', 'Ready in 15 seconds', '5 free stories/month'].map(t => (
                  <span key={t} style={{ color: '#c9a8ff', fontSize: '12px' }}>
                    <i className="bi bi-check-circle-fill me-1" style={{ color: '#4ade80' }}></i>{t}
                  </span>
                ))}
              </div>
            </div>
            <div className="col-12 col-lg-5 text-center">
              <div style={{ fontSize: 'clamp(80px,14vw,120px)', lineHeight: 1 }}>{ageGroup.emoji}</div>
              <div className="mt-2 badge rounded-pill px-3 py-2" style={{ background: 'rgba(249,199,79,0.2)', color: '#f9c74f', fontSize: '14px', fontFamily: "'Fredoka One',cursive" }}>
                Ages {ageGroup.range}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value-first: watch a real personalised story before any signup */}
      <StoryPreviewWidget
        initialName={name}
        initialAge={previewAgeFor(ageGroup.range)}
        heading={`See ${name}'s bedtime story come to life`}
        subheading={`Watch a magical story starring ${name} appear in seconds — free, no signup needed.`}
      />

      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(22px,4vw,34px)' }}>
            Why {name}'s stories are matched to age {ageGroup.range}
          </h2>
          <div className="row g-3 justify-content-center">
            {[
              { icon: '📖', title: 'Perfect length', body: `Stories sized just right for ${name}'s attention span at ages ${ageGroup.range} — not too long, not too short.` },
              { icon: '💬', title: 'Right vocabulary', body: `Words and sentences calibrated for ages ${ageGroup.range} — building ${name}'s language skills naturally through story.` },
              { icon: '💡', title: 'Right lessons', body: `Age-appropriate moral lessons that resonate with where ${name} is in life right now — woven naturally into every adventure.` },
            ].map(f => (
              <div className="col-12 col-md-4" key={f.title}>
                <div className="text-center p-3 p-md-4 rounded-4 h-100" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>{f.icon}</div>
                  <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)', fontSize: '15px' }}>{f.title}</h5>
                  <p className="text-muted mb-0" style={{ fontSize: '14px' }}>{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
            Adventures waiting for {name} tonight
          </h2>
          <div className="row g-3">
            {storyTitles.map((title, i) => (
              <div className="col-12 col-md-6" key={i}>
                <div className="d-flex align-items-center gap-3 p-3 rounded-4 bg-white" style={{ border: '1px solid #e9d5ff' }}>
                  <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg,#6f42c1,#9b6fe8)', color: '#fff', fontWeight: 700, fontSize: '14px' }}>
                    {name[0]}
                  </div>
                  <span style={{ color: 'var(--text-main)', fontWeight: 600, fontSize: '14px', lineHeight: 1.4 }}>{title}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HowItWorks childLabel={name} />
      <FeatureCards />

      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-4" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,28px)' }}>
            Stories for {name} at every age
          </h2>
          <div className="row g-2 g-md-3 justify-content-center">
            {otherAges.map(a => (
              <div className="col-6 col-md-3" key={a.slug}>
                <Link to={`/bedtime-stories/for/${name.toLowerCase()}/age/${a.slug}`}
                  className="d-block text-center p-3 rounded-4 text-decoration-none"
                  style={{ border: '2px solid #e9d5ff', background: 'var(--card-bg)' }}>
                  <div style={{ fontSize: '26px', marginBottom: '6px' }}>{a.emoji}</div>
                  <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '13px' }}>{a.label}</div>
                  <div style={{ color: 'var(--text-soft)', fontSize: '11px' }}>Ages {a.range}</div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        heading={`Create ${name}'s story tonight`}
        sub={`Perfectly crafted for ages ${ageGroup.range} — personalised just for ${name}.`}
      />
    </>
  );
}

// ── THEME + LANGUAGE PAGE ─────────────────────────────────────────────────────
function ThemeLanguagePage({ theme, lang }) {
  const otherLangs = LANGUAGES.filter(l => l.slug !== lang.slug).slice(0, 8);
  const otherThemes = THEMES.filter(t => t.slug !== theme.slug).slice(0, 8);

  return (
    <>
      <SEO
        title={`${theme.label} Bedtime Stories in ${lang.label} — Free & Personalised`}
        description={`Free personalised ${theme.label.toLowerCase()} bedtime stories in ${lang.label} (${lang.native}), starring your child. Perfect for bilingual families — ready in seconds.`}
        canonical={`/bedtime-stories/themes/${theme.slug}/in/${lang.slug}`}
      />
      <SchemaMarkup schema={[
        buildBreadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Story Themes', path: '/bedtime-stories/themes' },
          { name: `${theme.label} Stories`, path: `/bedtime-stories/themes/${theme.slug}` },
          { name: `${theme.label} in ${lang.label}`, path: `/bedtime-stories/themes/${theme.slug}/in/${lang.slug}` },
        ]),
        buildFaqSchema(SEO_PAGE_FAQS),
      ]} />
      <Navbar />
      <Hero
        badge={`${theme.emoji} ${lang.emoji} ${theme.label} in ${lang.label}`}
        title={`${theme.label} Bedtime Stories in`}
        highlight={lang.label}
        description={`Create personalised ${theme.label.toLowerCase()} bedtime stories in ${lang.label} (${lang.native}) — starring your child by name. Perfect for bilingual families.`}
        ctaLabel={`Create a ${theme.label} Story in ${lang.label} Free`}
        visual={
          <div>
            <div style={{ fontSize: 'clamp(60px,10vw,90px)', lineHeight: 1 }}>{theme.emoji}</div>
            <div style={{ fontSize: 'clamp(40px,7vw,60px)', lineHeight: 1, marginTop: '8px' }}>{lang.emoji}</div>
          </div>
        }
        preview={{ initialInterests: previewInterestsFor(theme.label), heading: `See a ${theme.label.toLowerCase()} story come to life`, subheading: `Type your child's name and watch a ${theme.label.toLowerCase()} bedtime story appear — free, no signup needed.` }}
      />

      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <div className="row g-4 align-items-start">
            <div className="col-12 col-lg-6">
              <h2 className="fw-bold mb-3" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(20px,4vw,30px)' }}>
                Why {theme.label.toLowerCase()} stories in {lang.label} are special
              </h2>
              <p className="text-muted mb-3" style={{ fontSize: '15px', lineHeight: 1.8 }}>
                {theme.whyKidsLove}
              </p>
              <p className="text-muted mb-0" style={{ fontSize: '15px', lineHeight: 1.8 }}>
                {lang.bilingualBenefit}
              </p>
            </div>
            <div className="col-12 col-lg-6">
              <div className="p-3 p-md-4 rounded-4" style={{ background: 'var(--page-bg)', border: '1px solid #e9d5ff' }}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span style={{ fontSize: '22px' }}>{theme.emoji}</span>
                  <span style={{ fontSize: '22px' }}>{lang.emoji}</span>
                  <div>
                    <div style={{ color: 'var(--text-main)', fontWeight: 700, fontSize: '14px' }}>{theme.label} Story · {lang.label}</div>
                    <div style={{ color: '#7c3aed', fontSize: '12px' }}>Personalised for your child · {lang.native}</div>
                  </div>
                </div>
                {theme.storyTitles.slice(0, 3).map((title, i) => (
                  <div key={i} className="d-flex align-items-center gap-2 py-2"
                    style={{ borderBottom: i < 2 ? '1px solid #e9d5ff' : 'none' }}>
                    <i className="bi bi-book flex-shrink-0" style={{ color: '#6f42c1', fontSize: '13px' }}></i>
                    <span style={{ color: 'var(--text-body)', fontSize: '14px', fontWeight: 500 }}>{title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <HowItWorks childLabel="your child" />

      <section className="py-5" style={{ background: 'var(--page-bg)' }}>
        <div className="container py-2 px-4">
          <h2 className="fw-bold mb-4 text-center" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(18px,3vw,26px)' }}>
            {theme.label} facts for curious {lang.label}-speaking kids
          </h2>
          <div className="row g-3 justify-content-center">
            {theme.facts.map((fact, i) => (
              <div className="col-12 col-md-4" key={i}>
                <div className="p-3 p-md-4 rounded-4 h-100 bg-white" style={{ border: '1px solid #e9d5ff' }}>
                  <div className="mb-2" style={{ fontSize: '20px' }}>💡</div>
                  <p className="mb-0" style={{ color: 'var(--text-body)', fontSize: '14px', lineHeight: 1.7 }}>{fact}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FeatureCards />

      <section className="py-5 bg-white">
        <div className="container py-2 px-4">
          <h2 className="fw-bold text-center mb-3" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(18px,3vw,26px)' }}>
            More {theme.label} stories in other languages
          </h2>
          <div className="d-flex flex-wrap gap-2 justify-content-center mb-5">
            {otherLangs.map(l => (
              <Link key={l.slug} to={`/bedtime-stories/themes/${theme.slug}/in/${l.slug}`}
                className="btn btn-sm rounded-pill text-decoration-none"
                style={{ border: '1px solid #e9d5ff', color: '#7c3aed', background: 'var(--card-bg)', fontSize: '13px' }}>
                {l.emoji} {l.label}
              </Link>
            ))}
          </div>
          <h2 className="fw-bold text-center mb-3" style={{ fontFamily: "'Fredoka One',cursive", color: 'var(--text-main)', fontSize: 'clamp(18px,3vw,26px)' }}>
            More themes in {lang.label}
          </h2>
          <div className="d-flex flex-wrap gap-2 justify-content-center">
            {otherThemes.map(t => (
              <Link key={t.slug} to={`/bedtime-stories/themes/${t.slug}/in/${lang.slug}`}
                className="btn btn-sm rounded-pill text-decoration-none"
                style={{ border: '1px solid #e9d5ff', color: '#7c3aed', background: 'var(--card-bg)', fontSize: '13px' }}>
                {t.emoji} {t.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        heading={`Create your child's ${theme.label} story in ${lang.label} tonight`}
        sub={`Personalised in ${lang.native} — starring your child by name, ready in 15 seconds.`}
      />
    </>
  );
}

// ── Router components ─────────────────────────────────────────────────────────
export function ThemePageRouter() {
  const { theme } = useParams();
  const found = THEMES.find(t => t.slug === theme);
  if (!found) return <Navigate to="/bedtime-stories/themes" replace />;
  return <ThemePage theme={found} />;
}

export function LanguagePageRouter() {
  const { language } = useParams();
  const found = LANGUAGES.find(l => l.slug === language);
  if (!found) return <Navigate to="/bedtime-stories/languages" replace />;
  return <LanguagePage lang={found} />;
}

export function AgeGroupPageRouter() {
  const { age } = useParams();
  const found = AGE_GROUPS.find(a => a.slug === age);
  if (!found) return <Navigate to="/" replace />;
  return <AgeGroupPage ageGroup={found} />;
}

export function OccasionPageRouter() {
  const { occasion } = useParams();
  const found = OCCASIONS.find(o => o.slug === occasion);
  if (!found) return <Navigate to="/" replace />;
  return <OccasionPage occasion={found} />;
}

export function NamePageRouter() {
  const { name } = useParams();
  const capitalized = name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : null;
  if (!capitalized) return <Navigate to="/bedtime-stories/for" replace />;
  return <NamePage name={capitalized} />;
}

export function LessonPageRouter() {
  const { lesson } = useParams();
  const found = LESSONS.find(l => l.slug === lesson);
  if (!found) return <Navigate to="/" replace />;
  return <LessonPage lesson={found} />;
}

export function StoryLengthPageRouter() {
  const { length } = useParams();
  const found = STORY_LENGTHS.find(l => l.slug === length);
  if (!found) return <Navigate to="/" replace />;
  return <StoryLengthPage length={found} />;
}

export function GenderPageRouter() {
  const { gender } = useParams();
  const found = GENDERS.find(g => g.slug === gender);
  if (!found) return <Navigate to="/" replace />;
  return <GenderPage gender={found} />;
}

export function RelationshipPageRouter() {
  const { rel } = useParams();
  const found = RELATIONSHIPS.find(r => r.slug === rel);
  if (!found) return <Navigate to="/" replace />;
  return <RelationshipPage rel={found} />;
}

export function CountryPageRouter() {
  const { country } = useParams();
  const found = COUNTRIES.find(c => c.slug === country);
  if (!found) return <Navigate to="/" replace />;
  return <CountryPage country={found} />;
}

export function HolidayPageRouter() {
  const { holiday } = useParams();
  const found = HOLIDAYS.find(h => h.slug === holiday);
  if (!found) return <Navigate to="/" replace />;
  return <HolidayPage holiday={found} />;
}

export function SiblingsPageRouter() {
  const { sibling } = useParams();
  const found = SIBLINGS.find(s => s.slug === sibling);
  if (!found) return <Navigate to="/" replace />;
  return <SiblingsPage sibling={found} />;
}

export function GiftPageRouter() {
  const { gift } = useParams();
  const found = GIFTS.find(g => g.slug === gift);
  if (!found) return <Navigate to="/" replace />;
  return <GiftPage gift={found} />;
}

export function ComparisonPageRouter() {
  const { comp } = useParams();
  const found = COMPARISONS.find(c => c.slug === comp);
  if (!found) return <Navigate to="/" replace />;
  return <ComparisonPage comp={found} />;
}

export function BilingualPageRouter() {
  const { bili } = useParams();
  const found = BILINGUAL.find(b => b.slug === bili);
  if (!found) return <Navigate to="/bedtime-stories/bilingual/bilingual" replace />;
  return <BilingualPage bili={found} />;
}

export function AnimalPageRouter() {
  const { animal } = useParams();
  const found = ANIMALS.find(a => a.slug === animal);
  if (!found) return <Navigate to="/bedtime-stories/animals" replace />;
  return <AnimalPage animal={found} />;
}

export function SettingPageRouter() {
  const { setting } = useParams();
  const found = SETTINGS.find(s => s.slug === setting);
  if (!found) return <Navigate to="/bedtime-stories/settings" replace />;
  return <SettingPage setting={found} />;
}

export function MoodPageRouter() {
  const { mood } = useParams();
  const found = MOODS.find(m => m.slug === mood);
  if (!found) return <Navigate to="/bedtime-stories/mood" replace />;
  return <MoodPage mood={found} />;
}

export function NameThemePageRouter() {
  const { name, theme } = useParams();
  const capitalized = name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : null;
  const foundTheme = THEMES.find(t => t.slug === theme);
  if (!capitalized || !foundTheme) return <Navigate to="/bedtime-stories/for" replace />;
  return <NameThemePage name={capitalized} theme={foundTheme} />;
}

export function NameAgePageRouter() {
  const { name, age } = useParams();
  const capitalized = name ? name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() : null;
  const foundAge = AGE_GROUPS.find(a => a.slug === age);
  if (!capitalized || !foundAge) return <Navigate to="/bedtime-stories/for" replace />;
  return <NameAgePage name={capitalized} ageGroup={foundAge} />;
}

export function ThemeLanguagePageRouter() {
  const { theme, language } = useParams();
  const foundTheme = THEMES.find(t => t.slug === theme);
  const foundLang = LANGUAGES.find(l => l.slug === language);
  if (!foundTheme || !foundLang) return <Navigate to="/bedtime-stories/themes" replace />;
  return <ThemeLanguagePage theme={foundTheme} lang={foundLang} />;
}

export { ThemesIndexPage, LanguagesIndexPage, NamesIndexPage, FreePage, AnimalsIndexPage, SettingsIndexPage, MoodsIndexPage };
